/*
 * Keyboard shortcuts for manually-triggered AI automations, active on object
 * pages only.
 *
 * Automations run *on* an object: the object being viewed is passed as the
 * automation's input, exactly like the object top bars' "Run automation"
 * action (Mod+K, which opens the palette to pick an automation). A bound
 * hotkey is that same flow with the picking step skipped — so it is NOT
 * app-wide: it only fires while an object page (task, event, person, page,
 * or custom object) is displayed.
 *
 * The listener is created once (Routine.vue) because the automation
 * configuration screen also needs the mapping API (`get`/`set`), but it is
 * inert until an object page declares the object it displays through
 * `activate()` (called by the object top bars, released on unmount).
 *
 * Precedence: within an object page, automation hotkeys deliberately take
 * precedence over every other in-app shortcut. The `v-hotkey` directive
 * (controller/hotkeys.ml) binds bubble-phase keydown listeners on
 * `document`, and editor/input handlers sit on their own elements — so a
 * *capture-phase* listener on `window` always sees the event first. When a
 * bound hotkey matches, the event is consumed (`preventDefault` +
 * `stopImmediatePropagation`) before any other handler runs, letting users
 * knowingly shadow built-in shortcuts.
 *
 * Two guards temper that precedence:
 * - hotkeys without a non-shift modifier (e.g a bare letter) are ignored
 *   while an editable element is focused, so they cannot hijack typing;
 * - events originating inside a hotkey-recording field (marked with the
 *   `data-hotkey-capture` attribute, see HotkeyCapture.vue) are ignored, so
 *   re-recording a combo does not run the automation it currently maps to.
 *
 * Storage: hotkeys are a device-local, per-account preference (like the
 * Electron OS-global hotkeys, they are not synced), persisted through the
 * account-namespaced browser store as `automationId -> Keymap.Key[]`, in the
 * physical-key representation produced by HotkeyCapture.vue. Entries whose
 * automation no longer exists, is disabled, or is no longer manual are
 * simply ignored at match time.
 */

import { ref, shallowRef, type Ref } from "vue";

import type {
  Controller,
  ReferenceId,
  ReleasableRef,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import type {
  Automation,
  ObjectContext,
  UseAutomations,
} from "@/components/ai/useAutomations";
import type { BaseLogger as Logger } from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { withRelease } from "@/ref";
import { keymap, type Keymap } from "@/utils/keymap";
import { isRecord } from "@/utils/unknown";

/** Browser-store key (account-namespaced) holding the hotkey mapping. */
const STORE_KEY = "automations:hotkeys";

/**
 * The object an object page displays, as the automation's input. Default
 * objects and pages already carry a `ReferenceId`; custom objects (graph
 * rows) are identified by position and resolved to a reference at trigger
 * time (mirrors palette.ts's `triggerAutomation`).
 */
export type AutomationHotkeyTarget =
  | { kind: "reference"; referenceId: ReferenceId }
  | {
      kind: "object";
      workspaceId: WorkspaceId;
      tableId: string;
      rowId: string;
    };

export type UseAutomationHotkeys = {
  /** The hotkey bound to an automation on this device, or null. */
  get: (automationId: string) => Keymap.Key[] | null;
  /** Bind (or, with null, unbind) a hotkey for an automation. */
  set: (automationId: string, keys: Keymap.Key[] | null) => void;
  /**
   * Declare the object currently displayed, arming the hotkeys while the
   * calling object page is mounted. The getter is polled at trigger time
   * (so it can follow a reactive prop) and may return null when the page
   * has no object yet. Returns the deactivation function, which the caller
   * must run on unmount; hotkeys are inert while no activation is live.
   */
  activate: (target: () => AutomationHotkeyTarget | null) => () => void;
};

/**
 * A hotkey decomposed for matching against keyboard events: the exact
 * modifier set plus a single non-modifier key.
 */
export type ParsedHotkey = {
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
  shift: boolean;
  key: Keymap.Key;
};

/**
 * Decompose a captured key sequence (as stored by HotkeyCapture.vue) into
 * modifiers plus exactly one main key. Returns null for sequences that
 * cannot be matched (no main key, several main keys, or an unsupported
 * modifier such as CapsLock).
 *
 * "Mod" is the physical Meta key: HotkeyCapture records key positions
 * (`KeyboardEvent.code`), where MetaLeft/MetaRight map to "Mod" on every
 * platform (see keymap.ts), so it must match `event.metaKey` — not the
 * platform-dependent "primary modifier".
 */
export function parseHotkey(keys: Keymap.Key[]): ParsedHotkey | null {
  let alt = false;
  let ctrl = false;
  let meta = false;
  let shift = false;
  let main: Keymap.Key | null = null;
  for (const key of keys) {
    if (
      key === keymap.keyMod ||
      key === keymap.keyMeta ||
      key === keymap.keyCommand ||
      key === keymap.keySuper
    ) {
      meta = true;
    } else if (key === keymap.keyControl) {
      ctrl = true;
    } else if (key === keymap.keyAlt || key === keymap.keyOption) {
      alt = true;
    } else if (key === keymap.keyShift) {
      shift = true;
    } else if (keymap.isKeyModifier(key)) {
      // CapsLock and other unmatchable modifiers.
      return null;
    } else if (main === null) {
      main = key;
    } else {
      return null;
    }
  }
  return main === null ? null : { alt, ctrl, meta, shift, key: main };
}

/** Exact match: same main key (by physical position) and same modifier set. */
export function matchesHotkey(
  hotkey: ParsedHotkey,
  event: KeyboardEvent
): boolean {
  return (
    keymap.convertKeyboardEvent(event) === hotkey.key &&
    event.altKey === hotkey.alt &&
    event.ctrlKey === hotkey.ctrl &&
    event.metaKey === hotkey.meta &&
    event.shiftKey === hotkey.shift
  );
}

/**
 * Parse the persisted mapping (untrusted: hand-edited storage, older app
 * versions). Keeps only entries whose value is a matchable key sequence.
 */
function parseMapping(value: unknown): Record<string, Keymap.Key[]> {
  if (!isRecord(value)) return {};
  const result: Record<string, Keymap.Key[]> = {};
  for (const [automationId, keys] of Object.entries(value)) {
    if (!Array.isArray(keys)) continue;
    const strings: Keymap.Key[] = [];
    for (const key of keys) {
      if (typeof key !== "string") break;
      strings.push(key);
    }
    if (strings.length !== keys.length) continue;
    if (!parseHotkey(strings)) continue;
    result[automationId] = strings;
  }
  return result;
}

/** Typing into these must not trigger modifier-less hotkeys. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    return true;
  }
  return target.isContentEditable;
}

/** Manual = anything that is neither scheduled nor a reaction. */
function isManualTrigger(automation: Automation): boolean {
  const trigger = automation.trigger;
  return (
    !trigger ||
    (trigger.trigger !== "recurrent" && trigger.trigger !== "commit_pattern")
  );
}

/**
 * Resolve the displayed object to the automation input reference (same
 * resolution as palette.ts's `triggerAutomation`).
 */
async function resolveObjectContext(
  controller: Controller,
  target: AutomationHotkeyTarget
): Promise<ObjectContext> {
  if (target.kind === "reference") {
    return { objectId: target.referenceId };
  }
  const rawId = await controller.graph.object.id.make({
    workspace_id: target.workspaceId,
    table_id: target.tableId,
    row_id: target.rowId,
  });
  return { objectId: { kind: "object", id: rawId } satisfies ReferenceId };
}

/**
 * Install the listener and expose the per-account hotkey mapping.
 * Synchronous; the returned `release()` removes the listener. Meant to be
 * created once by Routine.vue and shared via `inject.automationHotkeys`;
 * the hotkeys only fire while an object page holds an `activate()` scope.
 */
export function useAutomationHotkeys(
  controller: Controller,
  baseLogger: Logger,
  accountId: string,
  automations: Ref<UseAutomations | undefined>
): Readonly<ReleasableRef<UseAutomationHotkeys>> {
  const logger = baseLogger.forModule("useAutomationHotkeys");
  const store = useBrowserStore(accountId);

  const mapping = ref<Record<string, Keymap.Key[]>>(
    parseMapping(store.get<unknown>(STORE_KEY, {}))
  );

  const get = (automationId: string): Keymap.Key[] | null =>
    mapping.value[automationId] ?? null;

  const set = (automationId: string, keys: Keymap.Key[] | null): void => {
    const next = { ...mapping.value };
    if (keys && parseHotkey(keys)) {
      next[automationId] = [...keys];
    } else {
      delete next[automationId];
    }
    mapping.value = next;
    store.set(STORE_KEY, next);
  };

  // Object pages activate a scope while mounted. A stack, because during a
  // navigation the incoming page can mount before the outgoing one unmounts
  // (Suspense); the most recent activation wins.
  const scopes: Array<() => AutomationHotkeyTarget | null> = [];
  const activate = (
    target: () => AutomationHotkeyTarget | null
  ): (() => void) => {
    scopes.push(target);
    return () => {
      const index = scopes.lastIndexOf(target);
      if (index !== -1) scopes.splice(index, 1);
    };
  };

  const findAutomation = (automationId: string): Automation | undefined => {
    const all = automations.value?.all.value;
    if (!all) return undefined;
    for (const byAgent of Object.values(all)) {
      for (const byAutomation of Object.values(byAgent)) {
        const automation = byAutomation[automationId];
        if (automation) return automation;
      }
    }
    return undefined;
  };

  const run = async (
    automation: Automation,
    target: AutomationHotkeyTarget
  ): Promise<void> => {
    const objectContext = await resolveObjectContext(controller, target);
    const runId = await automations.value?.run(
      automation.organizationId,
      automation.agentId,
      automation.id,
      objectContext
    );
    if (runId != null) {
      controller.notifications.notify(`Running "${automation.name}"`);
    }
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.isComposing || event.repeat) return;

    // Only on object pages: the object being viewed is the automation input.
    const scope = scopes.at(-1);
    if (!scope) return;

    let matchedId: string | null = null;
    let matchedHotkey: ParsedHotkey | null = null;
    for (const [automationId, keys] of Object.entries(mapping.value)) {
      const parsed = parseHotkey(keys);
      if (parsed && matchesHotkey(parsed, event)) {
        matchedId = automationId;
        matchedHotkey = parsed;
        break;
      }
    }
    if (matchedId === null || matchedHotkey === null) return;

    const eventTarget = event.target;
    // Never fire from inside a hotkey-recording field (HotkeyCapture.vue):
    // the user is redefining a combo, not invoking one.
    if (
      eventTarget instanceof Element &&
      eventTarget.closest("[data-hotkey-capture]")
    ) {
      return;
    }
    // Hotkeys without a real modifier must not hijack typing.
    const hasNonShiftModifier =
      matchedHotkey.alt || matchedHotkey.ctrl || matchedHotkey.meta;
    if (!hasNonShiftModifier && isEditableTarget(eventTarget)) return;

    const objectTarget = scope();
    if (!objectTarget) return;

    const automation = findAutomation(matchedId);
    if (!automation || !automation.enabled || !isManualTrigger(automation)) {
      return;
    }

    // Consume the event before any other in-app handler sees it: automation
    // hotkeys take precedence over every other shortcut by design.
    event.preventDefault();
    event.stopImmediatePropagation();

    logger.info("hotkey triggered automation", {
      automationId: automation.id,
      organizationId: automation.organizationId,
    });
    run(automation, objectTarget).catch((error: unknown) => {
      logger.error("hotkey automation run failed", {
        automationId: automation.id,
        error,
      });
    });
  };

  // Capture phase on `window`: runs before every bubble-phase in-app
  // shortcut handler (v-hotkey binds on `document`), giving automation
  // hotkeys precedence within object pages.
  window.addEventListener("keydown", onKeydown, { capture: true });
  const release = () => {
    window.removeEventListener("keydown", onKeydown, { capture: true });
  };

  const api: UseAutomationHotkeys = { get, set, activate };
  return withRelease(shallowRef(api), release);
}
